import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import SfhPrimaryButton from './SfhPrimaryButton';
import SectionLoader from '../../Loader/SectionLoader';
import RowItemElement from '../../Buy/RowItem';
import SelectPaymentOptions from '../../Buy/SelectPaymentOptions';
import AddDeliveryAddressModal from '../Address/DeliveryAddressModal';
import ProductImage from '../../LegacyOrder/ProductImage';
import PriceLockTimer from '../../Common/Timers/PriceLockTimer';
import {
  findDefaultPaymentMethod,
  formatPaymentMethodLabel,
  mapCreditCardsToPaymentMethods,
} from './sellFromHomePaymentHelpers';
import { resolveSfhVaultSymbol } from './sellFromHomeVaultHelpers';
import { ReactComponent as SfhCreditCard } from '../../../assets/images/svg/sfh-credit-card.svg';
import {
  PAYMENT_METHOD_TYPES,
  PAYMENT_METHOD_TYPES_ICONS,
} from '../../../util/enum/api/paymentTypes';
import { fetchCreditCards } from '../../../store/actions/creditCard/creditCardActions';
import { fetchProductSymbolPrices } from '../../../store/actions/products/productActions';
import {
  getQuotesForOrderSellFromHome,
  executeQuotesForOrderSellFromHome,
} from '../../../store/actions/orders/orderActions';
import { fetchAccountAddresses } from '../../../store/actions/account/accountActions';
import {
  getCurrentAccount,
  getCurrentAccountUid,
  selectAccountAddresses,
  selectDefaultMailingAddress,
} from '../../../store/selectors/accountSelectors';
import { selectCreditCards } from '../../../store/selectors/creditCardSelectors';
import { selectSettings } from '../../../store/selectors/settingsSelectors';
import { getProductBySymbol } from '../../../store/selectors/productsSelector';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import { PRODUCT_SYMBOL_PRICES_LOADING } from '../../../store/actions/products/productActionConstants';
import {
  ADD_ADDRESS_WIZARD,
  SIGNIFYD_SESSION_ID,
} from '../../../constants/sessionStorage';
import {
  removeFromSessionStorage,
  retrieveFromSessionStorage,
  storeInSessionStorage,
} from '../../../util/helpers/sessionStorageHelper';
import loadSignifydDeviceFingerprintScript, {
  loadSignifydDeviceFingerprintScriptWithoutSessionId,
} from '../../../signifydDeviceFingerprint';
import { generateSessionOrderId } from '../../../util/helpers/sinigfyd';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const PRE_AUTH_RATE = 0.05;
const DEFAULT_LOCK_SECONDS = 30;
const NOT_AVAILABLE = 'N/A';

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${themeColors.colorBorderPrimary};
  opacity: 0.4;
  width: 100%;
  margin: 0;

  ${({ $bleed }) =>
    $bleed &&
    `
      width: auto;
      margin-left: -16px;
      margin-right: -16px;
    `}
`;

const Footer = styled.div`
  > button {
    width: 100%;
  }
`;

const BottomSection = styled.div`
  margin-top: auto;
`;

const ProductRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ProductImageWrap = styled.div`
  flex-shrink: 0;

  > img {
    width: 72px;
    height: 72px;
    object-fit: contain;
    margin: 0;
  }
`;

const ProductTextCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const SellingLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
`;

const ProductCaption = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};
`;

const DropOffLine = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: ${themeColors.colorTextSecondary};
  text-align: center;
  padding: 14px 0;

  > b {
    font-weight: 600;
  }
`;

const TimerWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0;
`;

const PayoutRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
`;

const PayoutLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PayoutLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
`;

const PayoutSubLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const PayoutAmount = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorPayoutSuccess};
  white-space: nowrap;
`;

const PreAuthCard = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 6px;
  padding: 8px 12px;
  margin-top: 16px;
`;

const PreAuthLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PreAuthLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
`;

const PreAuthSubLabel = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const PreAuthAmount = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};
  white-space: nowrap;
`;

const FeeRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 8px 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 1.4;
  color: ${themeColors.colorTextPrimary};

  ${({ $isTotal }) =>
    $isTotal &&
    `
      font-weight: 600;
    `}
`;

const PaymentRow = styled.div`
  > div {
    border-top: none;
  }
`;

const SubmitErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  color: ${themeColors.colorError};
  text-align: center;
  margin-bottom: 8px;
`;

const SellFromHomeReview = ({
  selectedHit,
  quantity,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  selectedOriginAddress,
  handleSubmitOrder,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const account = useSelector(getCurrentAccount);
  const productBySymbol = useSelector(getProductBySymbol);
  const creditCards = useSelector(selectCreditCards);
  const accountAddresses = useSelector(selectAccountAddresses);
  const defaultMailingAddress = useSelector(selectDefaultMailingAddress);
  const settings = useSelector(selectSettings);
  const isLoadingPrice = useSelector(
    selectIsLoadingByActionTypes([PRODUCT_SYMBOL_PRICES_LOADING]),
  );

  const lockSeconds = Math.max(
    1,
    Math.round(
      (settings?.PricingRefreshFrequencyMilliseconds ??
        DEFAULT_LOCK_SECONDS * 1000) / 1000,
    ),
  );

  const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const [isAddAddressOpen, setIsAddAddressOpen] = useState(false);

  const [quote, setQuote] = useState(null);
  const [counter, setCounter] = useState(lockSeconds + 2);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const productCode = selectedHit?.Code;
  // The /quotes endpoint validates against the per-vault broker symbol, not
  // the catalog code — same resolution we use on the Quantity step.
  const vaultSymbol = useMemo(
    () => resolveSfhVaultSymbol(selectedHit, account),
    [selectedHit, account],
  );

  useEffect(() => {
    if (accountUid && productCode) {
      dispatch(
        fetchProductSymbolPrices({
          accountUid,
          symbol: productCode,
          side: 'sell',
        }),
      );
    }
  }, [accountUid, productCode, dispatch]);

  useEffect(() => {
    if (accountUid) {
      dispatch(fetchCreditCards({ accountUid }));
    }
  }, [accountUid, dispatch]);

  useEffect(() => {
    if (accountUid && !accountAddresses?.length) {
      dispatch(fetchAccountAddresses({ accountUid }));
    }
  }, [accountUid, accountAddresses?.length, dispatch]);

  useEffect(() => {
    const sessionId = retrieveFromSessionStorage(SIGNIFYD_SESSION_ID);
    const existingScript = document.getElementById('sig-api');
    if (!sessionId) {
      if (existingScript) {
        existingScript.dataset.orderSessionId = generateSessionOrderId();
        storeInSessionStorage(
          SIGNIFYD_SESSION_ID,
          existingScript.dataset.orderSessionId,
        );
        return;
      }
      loadSignifydDeviceFingerprintScript();
      const script = document.getElementById('sig-api');
      if (script) {
        storeInSessionStorage(
          SIGNIFYD_SESSION_ID,
          script.dataset.orderSessionId,
        );
      }
    } else if (!existingScript) {
      loadSignifydDeviceFingerprintScriptWithoutSessionId(sessionId);
    }
  }, []);

  const hitPrice =
    selectedHit?.ProductPrice ??
    selectedHit?.SellPrice ??
    selectedHit?.BidPrice ??
    selectedHit?.PricePerUnit ??
    selectedHit?.SellPricePerUnit;
  const apiPrice = productBySymbol?.ProductPrice;
  const unitPrice = Number(hitPrice ?? apiPrice ?? 0) || 0;

  const creditCardPaymentMethods = useMemo(
    () => mapCreditCardsToPaymentMethods(creditCards),
    [creditCards],
  );

  const defaultPaymentMethod = useMemo(
    () => findDefaultPaymentMethod(creditCardPaymentMethods),
    [creditCardPaymentMethods],
  );

  useEffect(() => {
    if (!selectedPaymentMethod && defaultPaymentMethod) {
      setSelectedPaymentMethod(defaultPaymentMethod);
    }
  }, [defaultPaymentMethod, selectedPaymentMethod, setSelectedPaymentMethod]);

  const activePaymentMethod = selectedPaymentMethod || defaultPaymentMethod;
  const isSegregated = Boolean(productBySymbol?.IsSegregated);

  // SFH ships FROM the user's selected verified origin address (picked on the
  // Shipping step). Cabrella's GetAvailableRates needs that origin to price
  // shipping/insurance. Until the Shipping step propagates the selection, fall
  // back to the default mailing address (then first verified, then first).
  const originAddress = useMemo(
    () =>
      selectedOriginAddress ||
      defaultMailingAddress ||
      accountAddresses?.find((a) => a?.IsVerified) ||
      accountAddresses?.[0],
    [selectedOriginAddress, defaultMailingAddress, accountAddresses],
  );
  const addressUid = originAddress?.AddressUid;

  const fetchQuote = useCallback(() => {
    if (!accountUid || !vaultSymbol || !quantity || !addressUid) return;
    dispatch(
      getQuotesForOrderSellFromHome({
        getQuotesPayload: {
          accountUid,
          symbolCode: vaultSymbol,
          quantityOrAmount: quantity,
          sideType: 'Sell',
          unitType: 'Quantity',
          paymentType: activePaymentMethod?.PaymentMethodType,
          paymentTypeUid: activePaymentMethod?.PaymentMethodUid,
          addressUid,
          isSegregated,
          addPaymentMethodData: false,
          orderEntryType: 'OrderEntry',
          isSellFromHome: true,
          // dropOff = origin (where the user ships from); destination is
          // derived server-side from the vault symbol.
          dropOffCountry: originAddress?.CountryIso3Code,
          dropOffState: originAddress?.StateRegion,
          dropOffCity: originAddress?.City,
          dropOffZip: originAddress?.PostalCode,
          dropOffAddress1: originAddress?.AddressLine,
          dropOffAddress2: originAddress?.AddressLine2,
        },
        onSuccess: (data) => setQuote(data),
        onError: () => {},
      }),
    );
  }, [
    accountUid,
    vaultSymbol,
    quantity,
    activePaymentMethod,
    addressUid,
    originAddress,
    isSegregated,
    dispatch,
  ]);

  useEffect(() => {
    fetchQuote();
  }, [fetchQuote]);

  // Reset the visible counter whenever the underlying quote query identity
  // changes (e.g. payment changes — we'll refetch on a fresh window).
  useEffect(() => {
    setCounter(lockSeconds + 2);
  }, [fetchQuote, lockSeconds]);

  // 1s ticker drives the visual countdown and triggers refetch when it
  // bottoms out. Display value is `counter - 2` (see PriceLockTimer), so the
  // user sees lockSeconds → 0. Paused while a submit is in-flight so the
  // price the user agreed to doesn't shift under them mid-call.
  useEffect(() => {
    if (isSubmitting) return undefined;
    const id = setInterval(() => {
      setCounter((c) => {
        if (c <= 2) {
          fetchQuote();
          return lockSeconds + 2;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [fetchQuote, lockSeconds, isSubmitting]);

  const qsi = quote?.QuoteStatusItem;
  const toFee = (value) => {
    if (value == null) return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  };
  const shippingFee = toFee(qsi?.ShippingFee);
  const insuranceFee = toFee(qsi?.ShippingInsuranceFee);
  const totalChargedToday =
    shippingFee != null && insuranceFee != null
      ? shippingFee + insuranceFee
      : null;
  const formatFee = (value) => {
    if (!quote) return t('sellWizard.sfhWizard.review.loading');
    return value == null ? NOT_AVAILABLE : formatMoneyNumeral(value);
  };
  const quotePayout = Number(
    qsi?.ClientNetPay ?? qsi?.TotalClientExecutionPrice,
  );
  const payout =
    Number.isFinite(quotePayout) && quotePayout > 0
      ? quotePayout
      : unitPrice * quantity;
  const preAuthAmount = payout * PRE_AUTH_RATE;

  const handleSubmit = () => {
    if (typeof handleSubmitOrder !== 'function') return;
    const quoteUid = quote?.QuoteUid;
    if (!quoteUid) {
      setSubmitError(t('sellWizard.sfhWizard.review.quoteUnavailable'));
      return;
    }
    setSubmitError('');
    setIsSubmitting(true);
    dispatch(
      executeQuotesForOrderSellFromHome({
        executeQuotePayload: {
          quoteUid,
          signifydSessionId:
            retrieveFromSessionStorage(SIGNIFYD_SESSION_ID) || 'null',
        },
        isLoading: setIsSubmitting,
        onSuccess: (data) => {
          handleSubmitOrder({
            unitPrice,
            payout,
            preAuthAmount,
            shippingFee,
            insuranceFee,
            paymentMethod: activePaymentMethod,
            submittedAt: new Date().toISOString(),
            quoteUid,
            expiresOnUtc: quote?.ExpiresOnUtc,
            transactionUid: data?.TransactionUid,
            deliveryTransactionUid: data?.DeliveryTransactionUid,
            referenceNumber: data?.TransactionUid,
          });
        },
        onError: (errorMessage) => {
          setSubmitError(
            typeof errorMessage === 'string' && errorMessage
              ? errorMessage
              : t('sellWizard.sfhWizard.review.submitFailed'),
          );
        },
      }),
    );
  };

  return (
    <SectionLoader isLoading={isLoadingPrice || isSubmitting}>
      <ModalBody>
        <ProductRow>
          <ProductImageWrap>
            <ProductImage productCode={productCode} width={72} height={72} />
          </ProductImageWrap>
          <ProductTextCol>
            <SellingLabel>
              {t('sellWizard.sfhWizard.review.selling')}
            </SellingLabel>
            <ProductCaption>
              {t('sellWizard.sfhWizard.review.productLine', {
                count: quantity,
                caption: `${selectedHit?.WeightCaption ?? ''} ${
                  selectedHit?.Caption ?? ''
                }`.trim(),
              })}
            </ProductCaption>
          </ProductTextCol>
        </ProductRow>

        <Divider />

        <DropOffLine>
          {t('sellWizard.sfhWizard.review.dropOff')
            .split('FedEx Ship Center')
            .map((part, idx, arr) => (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={idx}>
                {part}
                {idx < arr.length - 1 && <b>FedEx Ship Center</b>}
              </React.Fragment>
            ))}
        </DropOffLine>

        <Divider />

        <TimerWrap>
          <PriceLockTimer
            counter={counter}
            label={t('sellWizard.sfhWizard.review.priceLocked', {
              seconds: lockSeconds,
            })}
            labelColor={themeColors.colorTextSecondary}
          />
        </TimerWrap>

        <PayoutRow>
          <PayoutLeft>
            <PayoutLabel>
              {t('sellWizard.sfhWizard.review.payoutReceivable')}
            </PayoutLabel>
            <PayoutSubLabel>
              {t('sellWizard.sfhWizard.review.afterVerification')}
            </PayoutSubLabel>
          </PayoutLeft>
          <PayoutAmount data-cy="text-sfh-review-payout">
            {formatMoneyNumeral(payout)}
          </PayoutAmount>
        </PayoutRow>

        <Divider />

        <PreAuthCard>
          <PreAuthLeft>
            <PreAuthLabel>
              {t('sellWizard.sfhWizard.review.preAuthAmount')}
            </PreAuthLabel>
            <PreAuthSubLabel>
              {t('sellWizard.sfhWizard.review.preAuthHold')}
            </PreAuthSubLabel>
          </PreAuthLeft>
          <PreAuthAmount data-cy="text-sfh-review-preauth">
            {formatMoneyNumeral(preAuthAmount)}
          </PreAuthAmount>
        </PreAuthCard>

        <FeeRow>
          <span>{t('sellWizard.sfhWizard.review.shippingFee')}</span>
          <span>{formatFee(shippingFee)}</span>
        </FeeRow>
        <FeeRow>
          <span>{t('sellWizard.sfhWizard.review.insuranceFee')}</span>
          <span>{formatFee(insuranceFee)}</span>
        </FeeRow>
        <FeeRow $isTotal>
          <span>{t('sellWizard.sfhWizard.review.totalCharged')}</span>
          <span>{formatFee(totalChargedToday)}</span>
        </FeeRow>

        <BottomSection>
          <Divider $bleed />

          {activePaymentMethod && (
            <PaymentRow>
              <RowItemElement
                label={t('sellWizard.sfhWizard.review.payWith')}
                value={
                  <>
                    {activePaymentMethod.PaymentMethodType ===
                    PAYMENT_METHOD_TYPES.CREDIT_CARD ? (
                      <SfhCreditCard />
                    ) : (
                      PAYMENT_METHOD_TYPES_ICONS[
                        activePaymentMethod.PaymentMethodType
                      ]
                    )}
                    {formatPaymentMethodLabel(activePaymentMethod, t)}
                  </>
                }
                clickLabel={t('sellWizard.sfhWizard.quantity.change')}
                onClick={() => setIsPaymentDrawerOpen(true)}
              />
            </PaymentRow>
          )}

          <Footer>
            {submitError && (
              <SubmitErrorText data-cy="text-sfh-review-submit-error">
                {submitError}
              </SubmitErrorText>
            )}
            <SfhPrimaryButton
              // Disabled while a submit is in flight (avoid duplicate orders) and
              // while the quote is still loading — until it resolves the shipping
              // and insurance fees show loading text, so the order total isn't final.
              disabled={isSubmitting || !quote}
              loading={isSubmitting}
              onClick={handleSubmit}
              data-cy="button-sfh-preauth-submit"
            >
              {t('sellWizard.sfhWizard.review.submit')}
            </SfhPrimaryButton>
          </Footer>
        </BottomSection>
      </ModalBody>

      <SelectPaymentOptions
        open={isPaymentDrawerOpen}
        setOpen={setIsPaymentDrawerOpen}
        paymentMethods={creditCardPaymentMethods}
        selectedPaymentMethod={activePaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        accountUid={accountUid}
        isAddPaymentMethodOpen={isAddPaymentMethodOpen}
        setIsAddPaymentMethodOpen={setIsAddPaymentMethodOpen}
        isAddAddressOpen={isAddAddressOpen}
        setIsAddAddressOpen={setIsAddAddressOpen}
        wizardData={{}}
        setWizardData={() => {}}
        setSuccessToast={() => {}}
        creditCardOnly
      />
      {isAddAddressOpen && (
        <AddDeliveryAddressModal
          isOpen={isAddAddressOpen}
          onClose={() => {
            removeFromSessionStorage(ADD_ADDRESS_WIZARD);
            setIsAddAddressOpen(false);
          }}
          title={t('buyWizard.addYourDeliveryAddress')}
          page="address"
          avoidClearingStorage
          isAddAddressOpen={isAddAddressOpen}
          setIsAddAddressOpen={setIsAddAddressOpen}
        />
      )}
    </SectionLoader>
  );
};

SellFromHomeReview.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
    ProductPrice: PropTypes.number,
    SellPrice: PropTypes.number,
    BidPrice: PropTypes.number,
    PricePerUnit: PropTypes.number,
    SellPricePerUnit: PropTypes.number,
  }),
  quantity: PropTypes.number,
  selectedPaymentMethod: PropTypes.shape({}),
  setSelectedPaymentMethod: PropTypes.func,
  selectedOriginAddress: PropTypes.shape({
    AddressUid: PropTypes.string,
    AddressLine: PropTypes.string,
    AddressLine2: PropTypes.string,
    City: PropTypes.string,
    StateRegion: PropTypes.string,
    PostalCode: PropTypes.string,
    CountryIso3Code: PropTypes.string,
  }),
  handleSubmitOrder: PropTypes.func,
};

export default SellFromHomeReview;
